<template>
  <div class="industry-selector">
    <!-- User Profile Button -->
    <button class="profile-button" @click="showModal = true">
      <div class="profile-icon">
        <span class="industry-emoji">{{ currentIndustry.emoji }}</span>
      </div>
      <div class="profile-info">
        <span class="profile-name">{{ currentIndustry.name }}</span>
      </div>
      <svg class="chevron-icon" width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M4 6L8 10L12 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
      </svg>
    </button>

    <!-- Modal Overlay - Using Teleport to render at body level -->
    <Teleport to="body">
      <div v-if="showModal" class="modal-overlay" @click="showModal = false">
        <div class="modal-content" @click.stop>
          <div class="modal-header">
            <h2 class="modal-title">Select Industry</h2>
            <button class="modal-close" @click="showModal = false">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" fill="currentColor"/>
              </svg>
            </button>
          </div>
        
        <div class="modal-body">
          <p class="modal-description">
            Choose the agricultural industry you want to work within. Each industry has specialised data and workflows.
          </p>
          
          <div class="industry-grid">
            <button
              v-for="industry in industries"
              :key="industry.id"
              class="industry-card"
              :class="{ 'active': currentIndustry.id === industry.id }"
              @click="selectIndustry(industry)"
            >
              <span class="industry-card-emoji">{{ industry.emoji }}</span>
              <span class="industry-card-name">{{ industry.name }}</span>
              <span v-if="currentIndustry.id === industry.id" class="industry-card-check">
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M16.6667 5L7.50004 14.1667L3.33337 10" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
              </span>
            </button>
          </div>
        </div>
      </div>
      </div>
    </Teleport>
  </div>
</template>

<script>
export default {
  name: 'IndustrySelector',
  
  data() {
    return {
      showModal: false,
      industries: [
        {
          id: 'wool',
          name: 'Wool',
          emoji: '🐑',
          description: 'Wool production and processing',
          color: '#3A895F'
        },
        {
          id: 'beef',
          name: 'Beef',
          emoji: '🐄',
          description: 'Beef cattle and meat production',
          color: '#8B4513'
        },
        {
          id: 'grain',
          name: 'Grain',
          emoji: '🌾',
          description: 'Grain crops and cereals',
          color: '#DAA520'
        },
        {
          id: 'seafood',
          name: 'Seafood',
          emoji: '🐟',
          description: 'Fish and seafood production',
          color: '#4682B4'
        },
        {
          id: 'feedlot',
          name: 'Feedlot',
          emoji: '🌽',
          description: 'Livestock feeding operations',
          color: '#CD853F'
        },
        {
          id: 'pigs',
          name: 'Pigs',
          emoji: '🐷',
          description: 'Pig farming and pork production',
          color: '#FFC0CB'
        },
        {
          id: 'oysters',
          name: 'Oysters',
          emoji: '🦪',
          description: 'Oyster farming and aquaculture',
          color: '#708090'
        },
        {
          id: 'dairy',
          name: 'Dairy',
          emoji: '🥛',
          description: 'Dairy cattle and milk production',
          color: '#F0F8FF'
        },
        {
          id: 'poultry',
          name: 'Poultry',
          emoji: '🐔',
          description: 'Poultry farming and egg production',
          color: '#FFD700'
        }
      ],
      currentIndustry: {
        id: 'wool',
        name: 'Wool',
        emoji: '🐑',
        description: 'Wool production and processing',
        color: '#3A895F'
      }
    }
  },
  
  mounted() {
    // Load saved industry from localStorage or use default
    const savedIndustry = localStorage.getItem('selectedIndustry');
    if (savedIndustry) {
      const industry = this.industries.find(i => i.id === savedIndustry);
      if (industry) {
        this.currentIndustry = industry;
      }
    }
    
    // Emit the current industry on mount
    this.$emit('industry-changed', this.currentIndustry);
  },
  
  methods: {
    selectIndustry(industry) {
      this.currentIndustry = industry;
      localStorage.setItem('selectedIndustry', industry.id);
      this.$emit('industry-changed', industry);
      this.showModal = false;
      
      // Show a toast notification
      this.showToast(`Switched to ${industry.name} industry`);
    },
    
    showToast(message) {
      // Simple toast notification - you can enhance this
      const toast = document.createElement('div');
      toast.className = 'industry-toast';
      toast.textContent = message;
      document.body.appendChild(toast);
      
      setTimeout(() => {
        toast.classList.add('show');
      }, 100);
      
      setTimeout(() => {
        toast.classList.remove('show');
        setTimeout(() => {
          document.body.removeChild(toast);
        }, 300);
      }, 2000);
    }
  }
}
</script>

<style scoped>
.industry-selector {
  margin-top: auto;
  padding: 16px;
  border-top: 1px solid #E5E7EB;
}

.profile-button {
  width: 100%;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  background: #F9FAFB;
  border: 1px solid #E5E7EB;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.profile-button:hover {
  background: #F3F4F6;
  border-color: #D1D5DB;
}

.profile-icon {
  width: 40px;
  height: 40px;
  background: white;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
}

.profile-info {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  text-align: left;
}

.profile-name {
  font-size: 14px;
  font-weight: 600;
  color: #111827;
}

.profile-subtitle {
  font-size: 12px;
  color: #6B7280;
}

.chevron-icon {
  color: #9CA3AF;
  flex-shrink: 0;
}

/* Modal Styles */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  animation: fadeIn 0.2s ease;
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

.modal-content {
  background: white;
  border-radius: 12px;
  width: 90%;
  max-width: 600px;
  max-height: 80vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
  animation: slideUp 0.3s ease;
}

@keyframes slideUp {
  from {
    transform: translateY(20px);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
}

.modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 24px;
  border-bottom: 1px solid #E5E7EB;
}

.modal-title {
  font-size: 20px;
  font-weight: 600;
  color: #111827;
  margin: 0;
}

.modal-close {
  background: none;
  border: none;
  padding: 4px;
  cursor: pointer;
  color: #6B7280;
  border-radius: 4px;
  transition: all 0.2s ease;
}

.modal-close:hover {
  background: #F3F4F6;
  color: #111827;
}

.modal-body {
  padding: 24px;
  overflow-y: auto;
}

.modal-description {
  font-size: 14px;
  color: #6B7280;
  margin: 0 0 24px 0;
  line-height: 1.5;
}

.industry-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
  gap: 12px;
}

.industry-card {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 20px 16px;
  background: #F9FAFB;
  border: 2px solid #E5E7EB;
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.industry-card:hover {
  background: #F3F4F6;
  border-color: #D1D5DB;
  transform: translateY(-2px);
}

.industry-card.active {
  background: #ECFDF5;
  border-color: #3A895F;
}

.industry-card-emoji {
  font-size: 32px;
  line-height: 1;
}

.industry-card-name {
  font-size: 14px;
  font-weight: 600;
  color: #111827;
  text-align: center;
}

.industry-card-check {
  position: absolute;
  top: 8px;
  right: 8px;
  width: 24px;
  height: 24px;
  background: #3A895F;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
}

/* Toast Notification */
.industry-toast {
  position: fixed;
  bottom: 24px;
  left: 50%;
  transform: translateX(-50%) translateY(100px);
  background: #111827;
  color: white;
  padding: 12px 24px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
  z-index: 10000;
  opacity: 0;
  transition: all 0.3s ease;
}

.industry-toast.show {
  opacity: 1;
  transform: translateX(-50%) translateY(0);
}

/* Mobile Responsive */
@media (max-width: 768px) {
  .industry-grid {
    grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
  }
  
  .modal-content {
    width: 95%;
    max-height: 90vh;
  }
  
  .modal-header,
  .modal-body {
    padding: 16px;
  }
}
</style>
